import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Warning, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  trust_admin: "Trust Admin",
  hostel_admin: "Hostel Admin",
  school_admin: "School Admin",
  teacher: "Teacher",
  sac_admin: "SAC Admin",
};

export default function SecurityCard() {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState([]);
  const [required, setRequired] = useState(new Set());
  const [source, setSource] = useState("env");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/security");
      setAvailable(data.available_roles || []);
      setRequired(new Set(data.mfa_required_roles || []));
      setSource(data.source || "env");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load security settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (role) => {
    const next = new Set(required);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    setRequired(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/security", { mfa_required_roles: Array.from(required) });
      toast.success("2FA enforcement updated. Affected users will be prompted on their next request.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="vs-card p-6 text-center text-[var(--vs-text-secondary)]" data-testid="security-loading">Loading…</div>;
  }

  const enforced = required.size > 0;
  const warnLockout = required.has("super_admin");

  return (
    <div className="vs-card p-6 max-w-2xl space-y-5" data-testid="security-settings-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><ShieldCheck size={12} /> Mandatory 2FA</div>
          <h3 className="text-lg font-semibold mt-1">Two-Factor Authentication enforcement</h3>
          <p className="text-sm text-[var(--vs-text-secondary)] mt-1.5 leading-relaxed">
            Force chosen roles to set up TOTP-based 2FA before they can access protected endpoints.
            A user without 2FA enabled will receive an HTTP 428 with a prompt to complete setup
            at <code className="text-xs px-1 py-0.5 bg-[var(--vs-bg-subtle)] rounded">/app/security</code>.
          </p>
        </div>
        <Badge
          variant="outline"
          className={enforced ? "border-[var(--vs-success)]/40 text-[var(--vs-success)]" : "border-[var(--vs-text-secondary)]/40"}
          data-testid="security-status-badge"
        >
          {enforced ? `Enforced (${required.size})` : "Off"}
        </Badge>
      </div>

      <div className="text-[11px] uppercase tracking-widest text-[var(--vs-text-secondary)] font-mono">
        Source: <span data-testid="security-source">{source === "db" ? "Database (overrides env)" : "Environment variable"}</span>
      </div>

      <div className="space-y-2.5">
        {available.map((role) => (
          <div
            key={role}
            className="flex items-center justify-between p-3 rounded-lg border border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)]/40 transition-colors"
            data-testid={`security-role-row-${role}`}
          >
            <div className="flex items-center gap-3">
              <Switch
                checked={required.has(role)}
                onCheckedChange={() => toggle(role)}
                data-testid={`security-toggle-${role}`}
              />
              <div>
                <Label className="cursor-pointer font-medium">{ROLE_LABELS[role] || role}</Label>
                <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{role}</div>
              </div>
            </div>
            {required.has(role) && (
              <Badge variant="outline" className="border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)]">
                2FA required
              </Badge>
            )}
          </div>
        ))}
      </div>

      {warnLockout && (
        <div
          className="flex items-start gap-2.5 p-3 rounded-lg border-l-4 border-amber-500 bg-amber-50/60 text-amber-900 text-sm"
          data-testid="security-self-lockout-warning"
        >
          <Warning size={18} className="flex-shrink-0 mt-0.5" weight="bold" />
          <div>
            <div className="font-semibold">Heads-up: this will affect your own account.</div>
            <div className="text-xs mt-0.5">
              You&apos;ll be redirected to <code>/app/security</code> on your next API call until you finish 2FA setup.
              Plan to scan the QR code with Google Authenticator / Authy right after saving.
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {dirty && (
          <Button variant="outline" onClick={load} disabled={saving} data-testid="security-cancel">
            Revert
          </Button>
        )}
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]"
          data-testid="security-save"
        >
          <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
